import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldQuestion, ChevronLeft, Loader2, CheckCircle2, Ban, RefreshCw, FileWarning } from "lucide-react";

const ALERT = {
  red: { color: "#ef4444", label_it: "Dati sospetti", label_en: "Suspicious data", Icon: ShieldAlert },
  yellow: { color: "#b45309", label_it: "Da verificare", label_en: "To review", Icon: ShieldQuestion },
  green: { color: "#16a34a", label_it: "Affidabile", label_en: "Reliable", Icon: ShieldCheck },
};
const BM_LABEL = { full: ["Peso pieno", "Full weight"], reduced: ["Peso ridotto", "Reduced weight"], review: ["In verifica (escluso)", "Under review (excluded)"], blocked: ["Bloccato", "Blocked"] };
const FILE_LABEL = { accepted: ["Accettato", "Accepted"], quarantine: ["In quarantena", "Quarantine"], rejected: ["Rifiutato", "Rejected"] };

function Dot({ level, lang }) {
  const a = ALERT[level] || ALERT.yellow;
  return <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: a.color }}>
    <span className="w-2.5 h-2.5 rounded-full" style={{ background: a.color }} /> {lang === "en" ? a.label_en : a.label_it}
  </span>;
}

export default function DataQualityTab() {
  const { lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/data-quality"); setRows(data.structures || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (pid) => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/data-quality/${pid}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); if (detail) await openDetail(detail.property_id); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const recompute = (pid) => act(() => api.post(`/admin/data-quality/${pid}/recompute`), L("Ricalcolato", "Recomputed"));
  const setOverride = (pid, override) => act(() => api.post(`/admin/data-quality/${pid}/benchmark`, { override }), L("Aggiornato", "Updated"));
  const setVerified = (pid, verified) => act(() => api.post(`/admin/data-quality/${pid}/verify`, { verified }), L("Aggiornato", "Updated"));
  const setFileStatus = (pid, batch_id, status) => act(() => api.post(`/admin/data-quality/${pid}/file-status`, { batch_id, status }), L("File aggiornato", "File updated"));

  if (loading) return <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  if (detail) {
    const b = detail.trust_breakdown || {};
    const sig = b.verification_signals || {};
    return (
      <div className="space-y-6" data-testid="dq-detail">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="dq-back">
          <ChevronLeft className="w-4 h-4" /> {L("Torna all'elenco", "Back to list")}
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-xl font-bold">{detail.name}</h3>
            <p className="text-sm text-muted-foreground">{detail.city} · {detail.property_type} · {detail.rooms} {L("camere", "rooms")} · {detail.owner_email}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold font-data" style={{ color: (ALERT[detail.data_alert] || ALERT.yellow).color }}>{detail.trust_score ?? "—"}<span className="text-sm text-muted-foreground">/100</span></div>
            <Dot level={detail.data_alert} lang={lang} />
          </div>
        </div>

        {/* Trust breakdown */}
        <div className="grid sm:grid-cols-3 gap-3">
          {[["data_quality", L("Qualità dati", "Data quality")], ["market_coherence", L("Coerenza mercato", "Market coherence")], ["verification", L("Verifica", "Verification")], ["account_age", L("Anzianità account", "Account age")], ["import_history", L("Storico import", "Import history")], ["stability", L("Stabilità", "Stability")]].map(([k, lbl]) => (
            <div key={k} className="rounded-lg border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{lbl}</div>
              <div className="font-data text-lg">{b[k] ?? "—"}</div>
            </div>
          ))}
        </div>

        {/* Benchmark + verification controls */}
        <div className="rounded-lg border border-border p-4 flex flex-wrap items-center gap-3" data-testid="dq-benchmark-controls">
          <span className="text-sm">{L("Stato benchmark", "Benchmark status")}: <b>{(BM_LABEL[detail.benchmark_status] || ["—"])[lang === "en" ? 1 : 0]}</b> · {L("peso", "weight")} {detail.benchmark_weight}</span>
          <div className="flex gap-2 ml-auto flex-wrap">
            <Button size="sm" variant={detail.benchmark_override === "auto" ? "default" : "outline"} onClick={() => setOverride(detail.property_id, "auto")} disabled={busy} data-testid="dq-override-auto">Auto</Button>
            <Button size="sm" variant={detail.benchmark_override === "force_include" ? "default" : "outline"} onClick={() => setOverride(detail.property_id, "force_include")} disabled={busy} data-testid="dq-override-include"><CheckCircle2 className="w-4 h-4 mr-1" />{L("Forza inclusione", "Force include")}</Button>
            <Button size="sm" variant={detail.benchmark_override === "force_exclude" ? "default" : "outline"} onClick={() => setOverride(detail.property_id, "force_exclude")} disabled={busy} data-testid="dq-override-exclude"><Ban className="w-4 h-4 mr-1" />{L("Blocca", "Block")}</Button>
            <Button size="sm" variant="outline" onClick={() => recompute(detail.property_id)} disabled={busy} data-testid="dq-recompute"><RefreshCw className="w-4 h-4 mr-1" />{L("Ricalcola", "Recompute")}</Button>
          </div>
        </div>
        <div className="rounded-lg border border-border p-4 flex flex-wrap items-center gap-3">
          <span className="text-sm">{L("Verifica proprietario", "Owner verification")}:</span>
          <div className="flex gap-2 text-xs">
            {Object.entries(sig).filter(([k]) => k !== "manual_verified").map(([k, v]) => (
              <span key={k} className="px-2 py-1 rounded" style={{ background: v ? "#16a34a18" : "#64748b18", color: v ? "#16a34a" : "#64748b" }}>{k}</span>
            ))}
          </div>
          <div className="ml-auto">
            {detail.owner_verified
              ? <Button size="sm" variant="outline" onClick={() => setVerified(detail.property_id, false)} disabled={busy} data-testid="dq-unverify">{L("Rimuovi verifica", "Unverify")}</Button>
              : <Button size="sm" onClick={() => setVerified(detail.property_id, true)} disabled={busy} data-testid="dq-verify"><ShieldCheck className="w-4 h-4 mr-1" />{L("Verifica manualmente", "Verify manually")}</Button>}
          </div>
        </div>

        {/* Anomalies */}
        <div>
          <h4 className="font-semibold mb-2 flex items-center gap-2"><FileWarning className="w-4 h-4" /> {L("Anomalie rilevate", "Detected anomalies")} ({detail.anomalies.length})</h4>
          {detail.anomalies.length === 0 ? <p className="text-sm text-muted-foreground">{L("Nessuna anomalia.", "No anomalies.")}</p> : (
            <div className="space-y-2" data-testid="dq-anomalies">
              {[...detail.anomalies].sort((a, b) => ({ high: 0, medium: 1, low: 2 }[a.level] - { high: 0, medium: 1, low: 2 }[b.level])).map((a, i) => (
                <div key={i} className="text-sm rounded border border-border p-2 flex gap-2">
                  <span className="w-2 h-2 rounded-full mt-1.5" style={{ background: a.level === "high" ? "#ef4444" : a.level === "medium" ? "#b45309" : "#64748b" }} />
                  {lang === "en" ? a.message_en : a.message_it}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Files */}
        <div>
          <h4 className="font-semibold mb-2">{L("File importati", "Imported files")}</h4>
          {detail.files.length === 0 ? <p className="text-sm text-muted-foreground">{L("Nessun file (dati caricati direttamente).", "No files (data loaded directly).")}</p> : (
            <div className="rounded-lg border border-border overflow-hidden" data-testid="dq-files">
              <table className="w-full text-sm">
                <thead className="bg-muted/50"><tr>
                  <th className="text-left px-3 py-2">{L("File", "File")}</th>
                  <th className="text-left px-3 py-2">{L("Qualità", "Quality")}</th>
                  <th className="text-left px-3 py-2">{L("Stato", "Status")}</th>
                  <th className="text-right px-3 py-2">{L("Azioni", "Actions")}</th>
                </tr></thead>
                <tbody>
                  {detail.files.map((f) => (
                    <tr key={f.batch_id} className="border-t border-border">
                      <td className="px-3 py-2">{f.filename || f.batch_id?.slice(0, 8)}</td>
                      <td className="px-3 py-2 font-data">{f.quality_score ?? "—"}</td>
                      <td className="px-3 py-2">{(FILE_LABEL[f.quality_status] || ["—"])[lang === "en" ? 1 : 0]}</td>
                      <td className="px-3 py-2 text-right space-x-1">
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setFileStatus(detail.property_id, f.batch_id, "accepted")} data-testid={`dq-file-accept-${f.batch_id}`}>{L("Accetta", "Accept")}</Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setFileStatus(detail.property_id, f.batch_id, "quarantine")} data-testid={`dq-file-quarantine-${f.batch_id}`}>{L("Quarantena", "Quarantine")}</Button>
                        <Button size="sm" variant="outline" disabled={busy} onClick={() => setFileStatus(detail.property_id, f.batch_id, "rejected")} data-testid={`dq-file-reject-${f.batch_id}`}>{L("Rifiuta", "Reject")}</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dq-list">
      <p className="text-sm text-muted-foreground mb-4">{L("Ogni struttura riceve un Trust Score. I file a bassa affidabilità NON entrano nel benchmark condiviso finché non li approvi.", "Each structure gets a Trust Score. Low-reliability files do NOT enter the shared benchmark until you approve them.")}</p>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr>
            <th className="text-left px-3 py-2">{L("Struttura", "Structure")}</th>
            <th className="text-left px-3 py-2">Trust</th>
            <th className="text-left px-3 py-2">{L("Alert", "Alert")}</th>
            <th className="text-left px-3 py-2">{L("Benchmark", "Benchmark")}</th>
            <th className="text-left px-3 py-2">{L("Qualità", "Quality")}</th>
            <th className="text-right px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.property_id} className="border-t border-border hover:bg-accent/40" data-testid={`dq-row-${r.property_id}`}>
                <td className="px-3 py-2"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.city} · {r.owner_email}</div></td>
                <td className="px-3 py-2 font-data font-bold" style={{ color: (ALERT[r.data_alert] || ALERT.yellow).color }}>{r.trust_score ?? "—"}</td>
                <td className="px-3 py-2">{r.data_alert ? <Dot level={r.data_alert} lang={lang} /> : <span className="text-xs text-muted-foreground">{L("non analizzato", "not analyzed")}</span>}</td>
                <td className="px-3 py-2 text-xs">{r.benchmark_status ? (BM_LABEL[r.benchmark_status] || ["—"])[lang === "en" ? 1 : 0] : "—"}{r.benchmark_weight != null && <span className="text-muted-foreground"> ({r.benchmark_weight})</span>}</td>
                <td className="px-3 py-2 font-data">{r.quality_score ?? "—"}</td>
                <td className="px-3 py-2 text-right">
                  {r.analyzed
                    ? <Button size="sm" variant="outline" onClick={() => openDetail(r.property_id)} data-testid={`dq-open-${r.property_id}`}>{L("Dettaglio", "Detail")}</Button>
                    : (r.has_data
                      ? <Button size="sm" variant="outline" disabled={busy} onClick={() => recompute(r.property_id)} data-testid={`dq-analyze-${r.property_id}`}>{L("Analizza", "Analyze")}</Button>
                      : <span className="text-xs text-muted-foreground">{L("nessun dato", "no data")}</span>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
